import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import { X, Shield, Leaf, Users, Eye, Phone, Info, Camera, MapPin, Ship, AlertTriangle, UserCheck, Recycle } from 'lucide-react';

// Linee guida smaltimento rifiuti
const WASTE_DISPOSAL_GUIDELINES = [
  {
    emoji: '🟦',
    title: '1. Plastica rigida',
    examples: 'bottiglie, tappi, cassette, contenitori, boe in plastica, frammenti di plastica dura',
    disposal: [
      'Raccolta plastica se pulita o poco sporca',
      'Indifferenziato se molto sporca o mista ad altri materiali'
    ],
    note: 'Oggetti grandi: usa "Segnalazioni"'
  },
  {
    emoji: '🟩',
    title: '2. Reti da pesca e corde',
    examples: 'reti leggere o pesanti, lenze, corde nautiche, filamenti',
    disposal: [
      'Indifferenziato (materiale composito difficile da riciclare)',
      'Ecocentro per reti pesanti o voluminose'
    ],
    note: 'Oggetti grandi: usa "Segnalazioni"'
  },
  {
    emoji: '🟨',
    title: '3. Metalli',
    examples: 'lattine, pezzi di ferro, ganci, moschettoni, parti metalliche',
    disposal: [
      'Raccolta metalli (o multimateriale, secondo il comune)',
      'Ecocentro per pezzi arrugginiti o voluminosi'
    ],
    note: 'Oggetti grandi: usa "Segnalazioni"'
  },
  {
    emoji: '🟫',
    title: '4. Legno lavorato',
    examples: 'cassette, pallet, assi trattate, parti di imbarcazioni',
    disposal: ['Ecocentro'],
    note: 'Oggetti grandi: usa "Segnalazioni"'
  },
  {
    emoji: '🟧',
    title: '5. Materiali misti o non identificabili',
    examples: 'plastica fusa, gomma, spugne sintetiche, scarpe, giocattoli, oggetti composti da più materiali',
    disposal: ['Indifferenziato'],
    note: 'Oggetti grandi: usa "Segnalazioni"'
  },
  {
    emoji: '⚫',
    title: '6. Rifiuti pericolosi',
    examples: 'batterie, bombolette, contenitori di carburante, oli, vernici, residui chimici',
    disposal: ['Ecocentro (mai nei cassonetti)'],
    note: 'Oggetti grandi o potenzialmente pericolosi: usa "Segnalazioni"'
  },
  {
    emoji: '🟤',
    title: '7. Vetro',
    examples: 'bottiglie, frammenti, vasetti',
    disposal: ['Raccolta vetro'],
    note: 'Oggetti grandi: usa "Segnalazioni"'
  },
  {
    emoji: '🟢',
    title: '8. Materiale naturale (da NON rimuovere)',
    examples: 'alghe, posidonia, legni naturali non trattati',
    disposal: ['Non rimuovere: sono fondamentali per l\'ecosistema e la protezione dell\'arenile'],
    note: 'Se spostati accidentalmente, lasciare sul posto o segnalare al comune tramite "Segnalazioni"'
  }
];

const GENERAL_RULES = [
  'Raccogli solo rifiuti artificiali (plastica, metallo, vetro, reti, gomma)',
  'Usa guanti e sacchi resistenti',
  'Se possibile, separa i materiali durante la raccolta',
  'Per oggetti voluminosi, pesanti o potenzialmente pericolosi, utilizza sempre la funzione "Segnalazioni" dell\'app',
  'Il conferimento dei rifiuti raccolti è consentito senza autorizzazioni speciali (Legge SalvaMare)'
];

const InfoSection = ({ onClose }) => {
  const { language } = useLanguage();
  return (
    <div className="fixed inset-0 bg-slate-950 z-50 overflow-y-auto">
      {/* Header con pulsante chiusura */}
      <div className="sticky top-0 bg-slate-950/95 backdrop-blur-xl border-b border-white/10 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <Info className="w-5 h-5 text-cyan-400" />
            </div>
            <h1 className="text-xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              INFORMAZIONI
            </h1>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="info-close-btn"
            className="w-10 h-10 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-all"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Contenuto */}
      <div className="max-w-4xl mx-auto px-4 py-6 pb-24 space-y-6">
        
        {/* Cos'è Respiro Blu */}
        <section className="bg-slate-900/60 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/likcbe5b_IMG_6596.jpeg" 
              alt="Respiro Blu Logo" 
              className="w-16 h-16 rounded-xl object-cover"
            />
            <h2 className="text-2xl font-bold text-cyan-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              COS'È RESPIRO BLU
            </h2>
          </div>
          <p className="text-slate-300 leading-relaxed">
            Infrastruttura digitale per il monitoraggio del Marine Litter. Fornisce evidenze digitali e dati di impatto 
            utili per supportare i bilanci di sostenibilità aziendali. L'app è pensata per volontari, pescherecci, 
            aziende, associazioni, fondazioni ed enti pubblici.
          </p>
        </section>

        {/* Linee Guida Smaltimento Rifiuti */}
        <section className="bg-slate-900/60 backdrop-blur-xl border border-emerald-500/30 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Recycle className="w-6 h-6 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-emerald-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              COME SMALTIRE I RIFIUTI
            </h2>
          </div>
          <p className="text-slate-300 leading-relaxed mb-4">
            Linee guida per la corretta suddivisione e gestione dei rifiuti trovati in spiaggia.
          </p>
          
          <div className="space-y-4">
            {WASTE_DISPOSAL_GUIDELINES.map((item, idx) => (
              <div key={idx} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                  <span className="text-xl">{item.emoji}</span>
                  {item.title}
                </h3>
                <p className="text-slate-400 text-sm mb-2">
                  <strong>Esempi:</strong> {item.examples}
                </p>
                <div className="text-sm text-slate-300 mb-2">
                  <strong className="text-emerald-400">Dove conferire:</strong>
                  <ul className="list-disc list-inside ml-2 mt-1">
                    {item.disposal.map((d, i) => (
                      <li key={i} className="text-slate-400">{d}</li>
                    ))}
                  </ul>
                </div>
                <p className="text-xs text-amber-400 italic">{item.note}</p>
              </div>
            ))}
            
            {/* Regole generali */}
            <div className="bg-cyan-500/10 rounded-xl p-4 border border-cyan-500/30 mt-4">
              <h3 className="font-bold text-cyan-400 mb-3 flex items-center gap-2">
                📌 Regole Generali
              </h3>
              <ul className="space-y-2">
                {GENERAL_RULES.map((rule, idx) => (
                  <li key={idx} className="text-slate-300 text-sm flex items-start gap-2">
                    <span className="text-cyan-400 mt-1">•</span>
                    {rule}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Tipi di Utente */}
        <section className="bg-slate-900/60 backdrop-blur-xl border border-violet-500/30 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-violet-400" />
            </div>
            <h2 className="text-xl font-bold text-violet-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              CHI PUÒ USARE L'APP
            </h2>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-800/50 rounded-lg p-3">
              <span className="text-cyan-400 font-semibold">Volontario</span>
              <p className="text-slate-400 text-xs mt-1">Singolo cittadino</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <span className="text-orange-400 font-semibold">Peschereccio</span>
              <p className="text-slate-400 text-xs mt-1">Fishing for Litter</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <span className="text-violet-400 font-semibold">Azienda</span>
              <p className="text-slate-400 text-xs mt-1">Impresa privata</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <span className="text-violet-400 font-semibold">Associazione</span>
              <p className="text-slate-400 text-xs mt-1">Organizzazione no-profit</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <span className="text-violet-400 font-semibold">Fondazione</span>
              <p className="text-slate-400 text-xs mt-1">Ente filantropico</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <span className="text-violet-400 font-semibold">Ente Pubblico</span>
              <p className="text-slate-400 text-xs mt-1">Comune, Provincia, Regione</p>
            </div>
          </div>
        </section>

        {/* Fishing for Litter */}
        <section className="bg-slate-900/60 backdrop-blur-xl border border-orange-500/30 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
              <Ship className="w-6 h-6 text-orange-400" />
            </div>
            <h2 className="text-xl font-bold text-orange-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              FISHING FOR LITTER
            </h2>
          </div>
          <p className="text-slate-300 leading-relaxed mb-4">
            Sezione dedicata ai pescherecci che raccolgono rifiuti durante le attività di pesca. 
            I rifiuti recuperati in mare vengono tracciati separatamente per monitorare l'impatto della pesca sostenibile.
          </p>
          <div className="bg-slate-800/50 rounded-xl p-4">
            <h3 className="text-white font-semibold mb-2">Come funziona:</h3>
            <ul className="text-slate-400 text-sm space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-orange-400">1.</span>
                Attiva il toggle "Raccolta in mare" nel form di raccolta
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-400">2.</span>
                Inserisci i kg raccolti per categoria
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-400">3.</span>
                Sulla mappa apparirà l'icona nave arancione 🚢
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-400">4.</span>
                Nella sezione "Mare" trovi lo storico delle sole raccolte in mare
              </li>
            </ul>
          </div>
        </section>

        {/* Pannello Admin */}
        <section className="bg-slate-900/60 backdrop-blur-xl border border-amber-500/30 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-amber-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              PANNELLO ADMIN
            </h2>
          </div>
          <p className="text-slate-300 leading-relaxed mb-4">
            Sezione riservata agli amministratori per monitorare e gestire l'attività della piattaforma.
          </p>
          <div className="bg-slate-800/50 rounded-xl p-4">
            <h3 className="text-white font-semibold mb-2">Funzionalità disponibili:</h3>
            <ul className="text-slate-400 text-sm space-y-2">
              <li className="flex items-start gap-2">
                <UserCheck className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <span><strong>Utenti Iscritti:</strong> Visualizza l'elenco completo di tutti gli utenti registrati, con nome, email, tipo utente e data di registrazione.</span>
              </li>
              <li className="flex items-start gap-2">
                <Eye className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <span><strong>Raccolte:</strong> Monitora tutte le raccolte effettuate dalla community.</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <span><strong>Emergenze SOS:</strong> Gestisci le segnalazioni di emergenza inviate dagli utenti.</span>
              </li>
            </ul>
          </div>
          <p className="text-slate-500 text-xs mt-3">
            Accesso limitato agli utenti con privilegi di amministratore.
          </p>
        </section>

        {/* Segnalazioni Rifiuti */}
        <section className="bg-slate-900/60 backdrop-blur-xl border border-yellow-500/30 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-400" />
            </div>
            <h2 className="text-xl font-bold text-yellow-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              SEGNALAZIONI RIFIUTI
            </h2>
          </div>
          <p className="text-slate-300 leading-relaxed mb-4">
            Hai visto un rifiuto ingombrante ma non puoi rimuoverlo? Segnalalo alla community! 
            Altri volontari potranno prenderlo in carico e rimuoverlo.
          </p>
          <div className="bg-slate-800/50 rounded-xl p-4">
            <h3 className="text-white font-semibold mb-2">Come funziona:</h3>
            <ul className="text-slate-400 text-sm space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-yellow-400">1.</span>
                Clicca su "Segnala" nella barra di navigazione
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-400">2.</span>
                Scatta una foto del rifiuto e descrivi cosa hai visto
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-400">3.</span>
                La segnalazione appare sulla mappa con un triangolo giallo ⚠️
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-400">4.</span>
                Un altro volontario può cliccare "Ci penso io" per prendere in carico
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-400">5. </span>
                Quando il rifiuto viene rimosso, si clicca "Risolto" e la segnalazione scompare
              </li>
            </ul>
          </div>
          <div className="mt-4 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-b-[14px] border-l-transparent border-r-transparent border-b-yellow-400"></div>
              <span className="text-slate-400 text-sm">Da rimuovere</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-b-[14px] border-l-transparent border-r-transparent border-b-orange-500"></div>
              <span className="text-slate-400 text-sm">In carico</span>
            </div>
          </div>
        </section>

        {/* Protocollo di Validazione */}
        <section className="bg-slate-900/60 backdrop-blur-xl border border-emerald-500/30 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Camera className="w-6 h-6 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-emerald-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              PROTOCOLLO DI VALIDAZIONE
            </h2>
          </div>
          
          <div className="space-y-4">
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                <Leaf className="w-4 h-4 text-emerald-400" />
                Pulizia Spiaggia / Mare
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                La foto è obbligatoria per documentare visivamente i rifiuti raccolti e convalidare il dato numerico (Kg) 
                inserito nel report. Se più persone partecipano alla raccolta, <strong>solo una persona</strong> deve caricare i dati 
                indicando il numero di partecipanti.
              </p>
            </div>
            
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-red-400" />
                SOS Fauna
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Funzione dedicata a creature marine in pericolo o carcasse. La foto serve all'identificazione della specie 
                e della posizione per le autorità.
              </p>
            </div>
          </div>
        </section>

        {/* Indicatori ESG */}
        <section className="bg-slate-900/60 backdrop-blur-xl border border-violet-500/30 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-violet-400 mb-4" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            INDICATORI ESG
          </h2>
          
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-emerald-500/10 to-transparent rounded-xl p-4 border-l-4 border-emerald-500">
              <h3 className="text-emerald-400 font-bold mb-2 flex items-center gap-2">
                <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded">E</span>
                Environmental
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Stima dell'impatto di decarbonizzazione evitata (prevenzione gas serra). Calcolato come Kg raccolti × 0.15.
              </p>
            </div>
            
            <div className="bg-gradient-to-r from-blue-500/10 to-transparent rounded-xl p-4 border-l-4 border-blue-500">
              <h3 className="text-blue-400 font-bold mb-2 flex items-center gap-2">
                <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">S</span>
                Social
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Misurazione del tempo dedicato alla cittadinanza attiva (ore uomo) e numero di partecipanti coinvolti.
              </p>
            </div>
            
            <div className="bg-gradient-to-r from-amber-500/10 to-transparent rounded-xl p-4 border-l-4 border-amber-500">
              <h3 className="text-amber-400 font-bold mb-2 flex items-center gap-2">
                <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">G</span>
                Governance
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Trasparenza assicurata da tracciamento GPS, documentazione fotografica e responsabilità di Massimo Marchiori.
              </p>
            </div>
          </div>
        </section>

        {/* Emergenza SOS */}
        <section className="bg-slate-900/60 backdrop-blur-xl border border-red-500/30 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
              <Phone className="w-6 h-6 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-red-400" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              EMERGENZA SOS
            </h2>
          </div>
          <p className="text-slate-300 leading-relaxed mb-4">
            Strumento per l'invio di segnalazioni geolocalizzate e pulsante di chiamata rapida alla Guardia Costiera.
          </p>
          <a 
            href="tel:1530"
            className="inline-flex items-center gap-3 bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-3 rounded-xl transition-all"
            data-testid="info-call-coastguard"
          >
            <Phone className="w-5 h-5" />
            CHIAMA 1530
          </a>
        </section>

        {/* Finalità dei Dati */}
        <section className="bg-slate-900/60 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-cyan-400 mb-4" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            FINALITÀ DEI DATI
          </h2>
          <p className="text-slate-300 text-sm leading-relaxed">
            I dati forniti in questo report fungono da evidenza digitale oggettiva per supportare i bilanci di sostenibilità 
            aziendali (DNF) e la rendicontazione dell'impatto ambientale verso stakeholder e istituzioni. 
            Questa piattaforma serve inoltre alle associazioni ambientaliste per monitorare con precisione il lavoro 
            svolto dai volontari sul territorio.
          </p>
        </section>

        {/* Copyright */}
        <section className="bg-slate-800/30 rounded-2xl p-6 text-center">
          <p className="text-slate-400 text-sm leading-relaxed">
            © 2026 Respiro Blu. Progetto di Massimo Marchiori — P. IVA 04056480231 — Verona.
          </p>
        </section>

      </div>
    </div>
  );
};

export default InfoSection;
